/** biome-ignore-all lint/suspicious/noConsole: debug */
import type { BlisRole } from "@blis/config";

/**
 * Granular Permission System
 *
 * This replaces the hierarchical role system with a more flexible permission-based approach.
 * Roles are now composed of specific permissions rather than being purely hierarchical.
 */

const Resources = [
  "accessions",
  "accession_comments",
  "accession_notes",
  "accession_uploads",
  "accession_views",
  "feedback",
  "holds",
  "instrument_results",
  "invoices",
  "materials",
  "notifications",
  "printers",
  "test_orders",
  "test_runs",
  "samples",
  "subjects",
  "results",
  "reports",
  "audit_logs",
  "insights",
] as const;

const Actions = ["read", "create", "update", "delete"] as const;

const ALL_PERMISSIONS = Actions.flatMap((action) =>
  Resources.map((resource) => `${action}:${resource}`),
);

export type PermissionResource = (typeof Resources)[number];

export type PermissionAction = (typeof Actions)[number];

export type Permission =
  | `${PermissionAction}:${PermissionResource}`
  | "validate:results"
  | "admin:all";

const commonPermissions: Permission[] = [
  "read:accessions",
  "read:accession_comments",
  "read:accession_uploads",
  "read:accession_views",
  "read:holds",
  "read:materials",
  "read:reports",
  "read:results",
  "read:samples",
  "read:subjects",
  "read:test_orders",

  "create:accession_comments",
  "create:feedback",
];

const commonLabPermissions: Permission[] = [
  "read:test_runs",
  "read:audit_logs",
  "read:accession_notes",
  "read:notifications",
  "read:instrument_results",
  "read:printers",
];

/**
 * Roles
 */

const blis_org_readonly: Permission[] = [...commonPermissions];

const blis_org_tech: Permission[] = [
  ...commonPermissions,
  "create:accessions",
  "create:subjects",
  "create:samples",
  "create:test_orders",
];

const blis_lab_readonly: Permission[] = [
  ...commonPermissions,
  ...commonLabPermissions,
];

const blis_lab_assistant: Permission[] = [
  ...commonPermissions,
  ...commonLabPermissions,
  "create:accessions",
  "create:subjects",
  "create:accession_comments",
  "create:accession_notes",
  "create:accession_uploads",
  "create:accession_views",
  "create:holds",
  "create:samples",
  "create:test_orders",
  "create:test_runs",
  "create:materials",

  "update:accessions",
  "update:accession_uploads",
  "update:accession_views",
  "update:holds",
  "update:materials",
  "update:subjects",
  "update:samples",
  "update:test_orders",
  "update:test_runs",

  "delete:accession_uploads",
  "delete:accession_views",
  "delete:samples",
  "delete:test_orders",
  "delete:test_runs",
];

const blis_lab_tech: Permission[] = [
  ...blis_lab_assistant,
  "create:results",
  "validate:results",
  "update:results",
  "create:reports",
  "create:instrument_results",
  "update:instrument_results",
];

const blis_lab_manager: Permission[] = [
  ...blis_lab_tech,
  "read:invoices",
  "read:insights",
  "update:reports",
  "update:invoices",

  "delete:accessions",
  "delete:materials",
  "delete:instrument_results",

  "admin:all",
];

const blis_lab_admin: Permission[] = [
  ...commonPermissions,
  ...commonLabPermissions,
  "read:invoices",
  "read:insights",
  "update:invoices",
  "admin:all",
];

export const ROLES = {
  blis_org_readonly,
  blis_org_tech,
  blis_lab_readonly,
  blis_lab_assistant,
  blis_lab_tech,
  blis_lab_manager,
  blis_lab_admin,
};

/**
 * Flat permission list for a role from {@link ROLES}.
 *
 * Cross-lab / cross-site restrictions are **not** applied here; they are enforced
 * by resource-scoped checks (`can` / `PERMISSION_RULES`), not by trimming this list.
 */
export function getEffectivePermissions(userRole: BlisRole): Permission[] {
  return ROLES[userRole] ?? [];
}

/**
 * Check if a user role has a specific permission
 */
export function hasPermission(
  userRole: BlisRole,
  permission: Permission,
): boolean {
  return getEffectivePermissions(userRole).includes(permission);
}

/**
 * Check if a user role has any of the specified permissions
 */
export function hasAnyPermission(
  userRole: BlisRole,
  permissions: Permission[],
): boolean {
  return permissions.some((permission) => hasPermission(userRole, permission));
}

/**
 * Check if a user role has all of the specified permissions
 */
export function hasAllPermissions(
  userRole: BlisRole,
  permissions: Permission[],
): boolean {
  return permissions.every((permission) => hasPermission(userRole, permission));
}

/**
 * Get all permissions for a role (same as {@link getEffectivePermissions}).
 */
export function getRolePermissions(userRole: BlisRole): Permission[] {
  return getEffectivePermissions(userRole);
}

/**
 * Check if role can perform a resource action (for backwards compatibility)
 */
export function canAccess(
  userRole: BlisRole,
  resource: string,
  action: "read" | "write" | "modify" | "admin",
): boolean {
  const permission = `${action}:${resource}` as Permission;
  return hasPermission(userRole, permission);
}

/**
 * Permission Groups for easier UI logic - this solves your UI maintainability issue!
//  */
// export const PERMISSION_GROUPS = {
//   // Basic access permissions
//   BASIC_READ: [
//     "read:results",
//     "read:reports",
//     "read:worklists",
//   ] as Permission[],
//   BASIC_WRITE: [
//     "write:worklists",
//     "write:requisitions",
//     "write:samples",
//   ] as Permission[],

//   // Result management
//   RESULTS_READ: ["read:results"] as Permission[],
//   RESULTS_WRITE: ["write:results", "modify:results"] as Permission[],
//   RESULTS_APPROVE: ["approve:results", "reject:results"] as Permission[],

//   // Report management
//   REPORTS_READ: ["read:reports"] as Permission[],
//   REPORTS_WRITE: ["write:reports", "modify:reports"] as Permission[],

//   // Admin capabilities
//   SYSTEM_ADMIN: ["admin:system", "admin:org_config"] as Permission[],
//   USER_ADMIN: ["admin:users", "write:users"] as Permission[],
//   LAB_ADMIN: ["admin:lab_config"] as Permission[],

//   // Data operations
//   DATA_EXPORT: ["export:data"] as Permission[],
//   DATA_IMPORT: ["import:data"] as Permission[],
// } as const;

/**
 * Check if role has permissions from a permission group
 */
export function hasPermissionGroup(
  userRole: BlisRole,
  group: Permission[],
): boolean {
  return hasAnyPermission(userRole, group);
}

/**
 * Permission-based middleware metadata interface for tRPC
 */
export interface PermissionMeta {
  // Single permission requirement
  requiredPermission?: Permission;

  // Any of these permissions (OR logic)
  requiredAnyPermissions?: Permission[];

  // All of these permissions (AND logic)
  requiredAllPermissions?: Permission[];

  // For backwards compatibility with existing role-based routes
  requiredRole?: BlisRole;
}

/**
 * Check if user has required permissions based on meta configuration.
 *
 * This is a coarse role-only check (see {@link ROLES}). For actions that depend
 * on which lab owns the accession/result, use resource-scoped `can` / services.
 */
export function hasRequiredPermissions(
  userRole: BlisRole,
  meta: PermissionMeta,
): boolean {
  // Handle single permission
  if (meta.requiredPermission) {
    return hasPermission(userRole, meta.requiredPermission);
  }

  // Handle any permissions (OR logic)
  if (meta.requiredAnyPermissions && meta.requiredAnyPermissions.length > 0) {
    return hasAnyPermission(userRole, meta.requiredAnyPermissions);
  }

  // Handle all permissions (AND logic)
  if (meta.requiredAllPermissions && meta.requiredAllPermissions.length > 0) {
    return hasAllPermissions(userRole, meta.requiredAllPermissions);
  }

  // Fallback to old role-based check for backwards compatibility
  // if (meta.requiredRole) {
  //   return hasRolePermission(userRole, meta.requiredRole);
  // }

  // If no permissions specified, deny access for security
  return false;
}

/**
 * Helper functions to create permission-based route metadata
 */
export function requirePermission(permission: Permission): PermissionMeta {
  return { requiredPermission: permission };
}

export function requireAnyPermission(
  ...permissions: Permission[]
): PermissionMeta {
  return { requiredAnyPermissions: permissions };
}

export function requireAllPermissions(
  ...permissions: Permission[]
): PermissionMeta {
  return { requiredAllPermissions: permissions };
}

export function requireRole(role: BlisRole): PermissionMeta {
  return { requiredRole: role };
}

/**
 * USAGE EXAMPLES FOR YOUR SPECIFIC USE CASE
 *
 * Your problem: blis_org_tech needs read + admin permissions but NO ability to modify results/reports
 *
 * Solution with this system:
 */

// 1. Routes that blis_org_tech CAN access:
// .meta(requirePermission("read:results"))        ✅ blis_org_tech has this
// .meta(requirePermission("admin:system"))        ✅ blis_org_tech has this
// .meta(requirePermission("admin:org_config"))    ✅ blis_org_tech has this
// .meta(requirePermission("export:data"))         ✅ blis_org_tech has this

// 2. Routes that blis_org_tech CANNOT access (solving your use case):
// .meta(requirePermission("modify:results"))      ❌ blis_org_tech lacks this
// .meta(requirePermission("modify:reports"))      ❌ blis_org_tech lacks this

// 3. Complex business logic:
// .meta(requireAnyPermission("read:results", "admin:system"))     ✅ blis_org_tech has both
// .meta(requireAllPermissions("read:results", "modify:results"))  ❌ blis_org_tech lacks modify

// 4. UI permission groups (solves maintainability):
// hasPermissionGroup(userRole, PERMISSION_GROUPS.SYSTEM_ADMIN)    ✅ blis_org_tech has admin
// hasPermissionGroup(userRole, PERMISSION_GROUPS.RESULTS_WRITE)   ❌ blis_org_tech lacks modify

export function debugPermissions() {
  for (const [role, permissions] of Object.entries(ROLES)) {
    const readPermissions = permissions.filter((p) => p.startsWith("read:"));
    const createPermissions = permissions.filter((p) =>
      p.startsWith("create:"),
    );
    const updatePermissions = permissions.filter((p) =>
      p.startsWith("update:"),
    );
    const deletePermissions = permissions.filter((p) =>
      p.startsWith("delete:"),
    );
    console.log("\n--------------------------------");
    console.log("Role:", role);
    console.log("--------------------------------");
    console.log("Can Read:");
    readPermissions.forEach((p) => {
      console.log(`- ${p}`);
    });
    console.log("Can Create:");
    createPermissions.forEach((p) => {
      console.log(`- ${p}`);
    });
    console.log("Can Update:");
    updatePermissions.forEach((p) => {
      console.log(`- ${p}`);
    });
    console.log("Can Delete:");
    deletePermissions.forEach((p) => {
      console.log(`- ${p}`);
    });
  }

  console.log("\n--------------------------------");
  console.log("Unused Permissions:");
  ALL_PERMISSIONS.forEach((p) => {
    if (
      !Object.values(ROLES)
        .flat()
        .includes(p as Permission)
    ) {
      console.log(`- ${p}`);
    }
  });
  console.log("--------------------------------");
}
